/**
 * @fileoverview Warn before closing or reloading the tab when a form is dirty.
 *
 * Purpose: Job settings saves are explicit (not autosave). This catches the
 * remaining case where the practitioner has typed into a section and then
 * closes the browser tab. In-app navigations are handled separately by the
 * page that owns the form.
 *
 * Used by: Job Settings (details, appointment, document setup tabs)
 */

import { useEffect } from "react";

/**
 * Registers a `beforeunload` listener while `isDirty` is true.
 *
 * @example
 * useUnsavedChangesWarning(identityHasChanges || bankHasChanges);
 */
export function useUnsavedChangesWarning(isDirty: boolean): void {
  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);
}
